import * as Yup from "yup";

import {
  AdvicePriceFormValues,
  OneTimePaymentAdvisorChoice,
} from "./AdviceCards/interfaces";

import { TextManager } from "Components/TextContext";
import { removeWhitespace } from "Formatting/index";
import { Advice, InsuranceAdvice } from "Store/advice/interfaces";
import { BooleanFormType } from "Store/interfaces/FormTypes";
import { isWithinValidOtpRange } from "./AdviceCards/DisabilityOneTimePaymentAdvisor/utils";
import { getAdviceRestrictions } from "./AdviceCards/utils";
import { checkShouldPurchaseAdviceFromAdvisor } from "./AdviceMessages/AdviceDiff/utils";

export const validationSchema = (
  isAdvisor: boolean,
  formText: any,
  lang: string,
  doubleYearlyIncome: number,
  adviceResult: Advice,
): Yup.ObjectSchema<AdvicePriceFormValues> => {
  const { getText } = TextManager;

  return Yup.object({
    disabilityCheckedToPurchase: Yup.boolean().required(),
    disabilityInsuredSumValue: Yup.number().required(),
    disabilityOneTimePayment: Yup.boolean().required(),
    disabilityOneTimePaymentValue: Yup.number().required(), //TODO: TEST THIS
    deathCheckedToPurchase: Yup.boolean().required(),
    deathInsuredSumValue: Yup.number().required(),
    criticalIllnessCheckedToPurchase: Yup.boolean().required(),
    criticalIllnessInsuredSumValue: Yup.number().required(),
    childProductCheckedToPurchase: Yup.boolean().required(),
    adviceDiffConsent: Yup.boolean().when(
      [
        "disabilityCheckedToPurchase",
        "disabilityInsuredSumValue",
        "disabilityOneTimePayment",
        "deathCheckedToPurchase",
        "deathInsuredSumValue",
        "criticalIllnessInsuredSumValue",
        "criticalIllnessCheckedToPurchase",
      ],
      {
        is: (
          disabilityCheckedToPurchase: boolean,
          disabilityInsuredSumValue: number,
          disabilityOneTimePayment: boolean,
          deathCheckedToPurchase: boolean,
          deathInsuredSumValue: number,
          criticalIllnessCheckedToPurchase: boolean,
          criticalIllnessInsuredSumValue: number,
        ) =>
          checkShouldPurchaseAdviceFromAdvisor(isAdvisor, adviceResult, {
            disabilityCheckedToPurchase,
            disabilityInsuredSumValue,
            disabilityOneTimePayment,
            deathCheckedToPurchase,
            deathInsuredSumValue,
            criticalIllnessCheckedToPurchase,
            criticalIllnessInsuredSumValue,
          }),
        then: (schema) =>
          schema
            .oneOf([true], getText("advicepage.advicediff.consentrequired"))
            .required(),
      },
    ),
  })
    .concat(
      disabilityOneTimePaymentAdvisorSchema(
        formText,
        lang,
        doubleYearlyIncome,
        adviceResult.advice.DISABILITY_FLEX,
      ),
    )
    .defined();
};

type AdvisorValues =
  | "isAdvisor"
  | "disabilityPaymentAfter3Months"
  | "disabilityOneTimePaymentAdvisorChoice"
  | "disabilityOneTimePaymentAdvisorValue"
  | "adviceComment";

const disabilityOneTimePaymentAdvisorSchema = (
  formText: any,
  lang: string,
  doubleYearlyIncome: number,
  advice: InsuranceAdvice,
): Yup.ObjectSchema<Pick<AdvicePriceFormValues, AdvisorValues>> => {
  const { getText } = TextManager;
  const disabilityAdviceOtpRestrictions = getAdviceRestrictions(advice, true);

  return Yup.object({
    isAdvisor: Yup.boolean().required(),
    disabilityPaymentAfter3Months: Yup.mixed<BooleanFormType>().when(
      ["isAdvisor", "disabilityCheckedToPurchase"],
      {
        is: (isAdvisor: boolean, disabilityCheckedToPurchase: boolean) =>
          isAdvisor && disabilityCheckedToPurchase,
        then: (schema) =>
          schema.required(
            getText("adviceform.validation.disabilitypaymentafter3months"),
          ),
      },
    ),
    disabilityOneTimePaymentAdvisorChoice:
      Yup.mixed<OneTimePaymentAdvisorChoice>().when(
        ["isAdvisor", "disabilityCheckedToPurchase"],
        {
          is: (isAdvisor: boolean, disabilityCheckedToPurchase: boolean) =>
            isAdvisor && disabilityCheckedToPurchase,
          then: (schema) =>
            schema.required(
              getText(
                "adviceform.validation.disabilityonetimepaymentadvisorchoice",
              ),
            ),
        },
      ),
    disabilityOneTimePaymentAdvisorValue: Yup.string().when(
      [
        "isAdvisor",
        "disabilityCheckedToPurchase",
        "disabilityOneTimePaymentAdvisorChoice",
      ],
      {
        is: (
          isAdvisor: boolean,
          disabilityCheckedToPurchase: boolean,
          disabilityOneTimePaymentAdvisorChoice: OneTimePaymentAdvisorChoice,
        ) =>
          isAdvisor &&
          disabilityCheckedToPurchase &&
          disabilityOneTimePaymentAdvisorChoice ===
            OneTimePaymentAdvisorChoice.CUSTOM_ONE_TIME_PAYMENT,
        then: (schema) =>
          schema
            .required(
              formText.advice.adviceCards.disability.disabilityOneTimePayment
                .error.required[lang],
            )
            .test(
              "disabilityOneTimePaymentAdvisorValue",
              formText.advice.adviceCards.disability.disabilityOneTimePayment
                .error.advisorAmount[lang],
              (value?: string) => {
                const paymentValue = value
                  ? Number(removeWhitespace(value))
                  : 0;
                return isWithinValidOtpRange(
                  doubleYearlyIncome,
                  paymentValue,
                  disabilityAdviceOtpRestrictions,
                );
              },
            ),
      },
    ),
    adviceComment: Yup.string(),
  }).defined();
};
